// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : viewer components
// =============================================================================
// ---Third-party imports
import React, {useState, useEffect, useMemo, useRef} from 'react'
import {Menu, Dropdown, Icon, Loader, Item, Input, Label, Image} from 'semantic-ui-react'

// ---Framework imports
import {
  range,
  get,
  has,
  mapItems,
  filterItems,
  dict,
  size,
  isArray,
  sorted,
  mergeDeep,
  isObject,
  callIfFunc,
} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {ucFirst, hashObj} from 'uix/utils'
import connect from 'uix/connect'
import {DebugFeature, XMenu, Toolbar} from 'uix/components'
import {hasGroup} from 'uix/auth'
import {Graphics} from 'uix/graphics'
import {
  useSelector,
  useActions,
  useDebounce,
  usePartContext,
  useInView,
  useCallback,
} from 'uix/hooks'
import {TitleList} from 'uix/frontend/components'

// ---Local imports
import {Mordor} from './mordor'
import {OptionSets} from './options'
import styles from './viewer.scss'
import {
  useImageLocs,
  useImageLocsPageSet,
  useInbookSearch,
  useViewer,
  ViewerContext,
  useWordData,
  indexImageLocs,
  useBoundingBoxGeometry,
  useHighlightingGeometry,
} from './hooks'
import {PageNavigator} from './pagenav'

// ---Exports
export {useGeometryCalculation} from './hooks'

// ---Globals

const getFocused = (otid, pages, byId) => {
  let descendantPage = byId[otid]
  let focused = 0
  if (descendantPage) {
    // fast path: its a datatype=3 id
    focused = descendantPage.flatIdx
  } else {
    // find substruct id in tree and find next page node
    for (let p of pages) {
      if (p.path.includes(otid)) {
        focused = byId[p.id].flatIdx
        break
      }
    }
  }
  return focused
}

const OptionSwitcher = ({pages, ui, setUI, vid, optionSet, ...itemprops}) => {
  let optsets = filterItems(OptionSets, (k, v) => k !== 'thumbs').map(([k, v]) => (
    <Dropdown.Item
      key={k}
      onClick={() => setUI({optionSet: {...ui.optionSet, [vid]: k}})}
      content={ucFirst(k)}
    />
  ))

  let trigger = (
    <>
      <Icon name="grid layout" /> {optionSet ? ucFirst(optionSet) : null}
    </>
  )

  return (
    <Menu.Item title="Change OptionSet" {...itemprops}>
      <Dropdown trigger={trigger}>
        <Dropdown.Menu>{optsets}</Dropdown.Menu>
      </Dropdown>
    </Menu.Item>
  )
}

const InbookSearch = ({otid, vid, viewer, pages, ...itemprops}) => {
  let [activeHitIndex, setActiveHitIndex] = useState(null)

  let externalQuery = useSelector(root => root.meta.request.query.query)
  let [searchTerm, setSearchTerm] = useState(null)
  let [bouncedTerm] = useDebounce(searchTerm, 500)

  useEffect(() => {
    setSearchTerm(null)
  }, [externalQuery])

  let clearInbookSearch = useActions('clearInbookSearch')

  let searchData = useInbookSearch({vid, otid, query: bouncedTerm || externalQuery})

  let clear = () => {
    clearInbookSearch(vid)
    setSearchTerm(null)
    setActiveHitIndex(null)
  }

  let focusNextHit = isMetaKey => {
    let otids = searchData?.searchResult?.otids || []
    let numHits = otids.length
    if (numHits > 0) {
      if (activeHitIndex === null) {
        var nextIndex = 0
      } else {
        var nextIndex = (isMetaKey ? activeHitIndex - 1 : activeHitIndex + 1) % numHits
      }
      setActiveHitIndex(nextIndex)

      let sortedIds = pages.map(p => String(p.id))
      let searchResult = sorted(otids, otid => sortedIds.indexOf(String(otid)))
      let id = searchResult[nextIndex]
      if (id) {
        viewer.scrollToId({id, duration: 1.0})
      }
    }
  }

  return (
    <>
      <XMenu.Item {...itemprops}>
        <Icon name="search" onClick={clear} />
        <Input
          value={searchTerm === null ? externalQuery || '' : searchTerm}
          onChange={(event, data) => setSearchTerm(data.value)}
          onKeyDown={event => event.key == 'Enter' && focusNextHit(event.shiftKey || event.ctrlKey)}
        />
      </XMenu.Item>
      <XMenu.Item content={`${get(searchData, 'searchResult.length', 0)}`} />
    </>
  )
}

const Navigation = ({focused, index, selected, scrollToId}) => {
  let {pages, byId} = index

  let paginationLabel = null
  let nextId = null
  let prevId = null

  if (selected.center) {
    let page = byId[selected.center]
    // we shouldnt try to reset selection, but store it relative to imageLocs root id
    if (!page) {
      log.warn('invalid selection: %o %o', selected.center, byId)
      return null
    }

    if (page.caption) {
      paginationLabel = (
        <Menu.Item key="caption" className="paginationLabel" content={page.caption} />
      )
    } else {
      paginationLabel = (
        <Menu.Item key="caption" className="paginationLabel" content={`type ${page.type}`} />
      )
    }
    nextId = page.flatIdx + 1 < pages.length ? pages[page.flatIdx + 1].id : null
    prevId = page.flatIdx > 0 ? pages[page.flatIdx - 1].id : null
    focused = page.flatIdx
  }

  return (
    <>
      <Menu.Item
        icon="backward"
        disabled={prevId === null}
        onClick={() => scrollToId({id: prevId, keepZoom: true})}
      />
      <Menu.Item className="pagenumLabel" content={`${focused + 1}/${index.pages.length}`} />
      {paginationLabel}
      <Menu.Item
        icon="forward"
        disabled={nextId === null}
        onClick={() => scrollToId({id: nextId, keepZoom: true})}
      />
    </>
  )
}

const ZoomSlider = ({zoom, alignZoom}) => (
  <Menu.Item className="zoomSlider">
    <input
      className={styles.menuSlider}
      type="range"
      title={zoom ? zoom.toFixed(0) : null}
      min={5}
      max={150}
      value={zoom ? parseInt(zoom ** (1 / 1.7)) : 0}
      onChange={event => alignZoom(parseInt(event.target.value ** 1.7))}
    />
  </Menu.Item>
)

const Debug = ({imageLocs, index}) => {
  let go = useActions('go')
  return (
    <Menu.Item
      content={`root: ${imageLocs.id}`}
      className="debug"
      onClick={() => go({id: imageLocs.id})}
    />
  )
}

export const WordLoader = ({otids}) => {
  if (!isArray(otids)) {
    otids = [otids]
  }
  // this is only a hack
  for (let otid of otids) {
    useWordData(otid)
  }
  return null
}

const LayerSwitcher = ({layers, onChange}) => {
  let toggle = name => ({...layers, [name]: !layers[name]})

  let viewer = useViewer()

  let build = (name, dd) => (
    <Menu.Item className="debug" active={layers[name]} onClick={() => onChange(toggle(name))}>
      {name}
      {dd}
    </Menu.Item>
  )

  let [loadAll, setLoadAll] = useState(null)

  let loadAllWords = () => {
    setLoadAll(viewer.index.pages.map(p => p.id))
  }

  let wordsDropdown = (
    <Dropdown>
      <Dropdown.Menu>
        <Dropdown.Item onClick={loadAllWords} text="Load All" icon={'play'} />
        {loadAll && <WordLoader otids={loadAll} />}
      </Dropdown.Menu>
    </Dropdown>
  )
  return <>{build('words', wordsDropdown)}</>
}

export const Viewer = connect(
  'Viewer',
  {
    storeMap: ['meta.user', 'meta.request.dname', 'meta.request.classname'],
    uiDefaults: {
      optionSet: {default: 'h2'},
      thumbsOn: null, // XXX: dynamic keying via vid due to <Toggle>
      debugOn: null, // XXX: dynamic keying via vid
      // XXX: versioning is broken due to dynamic keying
      __version__: 3,
    },
  },
  ({dname, user, vid = '', imageLocs, otid = null, ui, setUI, optionSet = null, go, classname}) => {
    let ref_viewer = useRef()
    let ref_thumbs = useRef()
    let replaceQueryParams = useActions(Actions.replaceQueryParams)

    let isDebug = hasGroup({user, name: 'debug'})

    if (!optionSet) {
      optionSet = get(ui.optionSet, vid, ui.optionSet.default)
    }

    if (!optionSet || optionSet.length < 2) {
      log.error('bad optionset %o', optionSet)
      optionSet = 'grid'
    }

    let scrollToId = scrollOpts => {
      if (ref_viewer.current) {
        ref_viewer.current.scrollToId(scrollOpts)
      }
    }

    let [zoom, setZoom] = useState(null)
    let alignZoom = zoom => {
      if (ref_viewer.current) {
        ref_viewer.current.setZoom(zoom)
      }
    }

    let initialSelState = {center: null, hover: null, clicked: null}
    let [selected, setSelected] = useState(initialSelState)
    // if first page is focused, do no align id in url
    let [skipFirstQueryReplace, setSkipFirstQueryReplace] = useState(true)
    let onViewerSelectionChange = ({change, selected: nextSelected}) => {
      if (change.center) {
        if (skipFirstQueryReplace) {
          setSkipFirstQueryReplace(false)
        } else {
          replaceQueryParams({id: nextSelected.center})
        }
        setSelected(nextSelected)
        //~ log(selected.center)
        if (ref_thumbs.current) {
          ref_thumbs.current.scrollToId({id: nextSelected.center, duration: 0.2})
        }
      }
    }
    // reset
    useEffect(() => setSkipFirstQueryReplace(true), [imageLocs])

    let onThumbSelectionChange = ({change, selected: nextSelected}) => {
      if (size(change.clicked) > 0) {
        let id = change.clicked[0]
        if (ref_viewer.current) {
          ref_viewer.current.scrollToId({id, duration: 1.0})
        }
      }
    }

    let [chosenTitle, setchosenTitle] = useState(null)

    // clear list-chosenTitle if external explicit imageLocs change
    useEffect(() => {
      setchosenTitle(null)
      setTitleListVisible(false)
      setSelected(initialSelState)
    }, [imageLocs])

    let onSelectListTitle = title => {
      if (classname == 'tree') {
        goTreeViewer(go, title)
      } else {
        setchosenTitle(title)
        setTitleListVisible(false)
      }
    }

    let chosenImageLocs = useImageLocs({
      otid: chosenTitle ? chosenTitle.otid : null,
      enabled: !imageLocs,
    })
    if (chosenImageLocs) {
      imageLocs = chosenImageLocs // chosenTitle.imageLocs
      otid = chosenTitle.pageid || chosenTitle.otid
    }

    let index = useMemo(() => indexImageLocs(imageLocs), [imageLocs])
    let focused = getFocused(otid, index.pages, index.byId)

    // if we have older selection data, check if page ids are still in imagelocs
    selected = dict(mapItems(selected, (key, value) => [key, index.byId[value] ? value : null]))

    let [titleListVisible, setTitleListVisible] = useState(false)

    let [layers, setLayers] = useState({})
    let isInbookSearchActive = useSelector(root => !!root.data.viewer.activeInBookSearches[vid])

    if (isInbookSearchActive) {
      layers = {...layers, search: true}
    }

    if (!imageLocs || titleListVisible) {
      return <TitleList onSelectTitle={onSelectListTitle} />
    }

    let thumbsActive = ui[`thumbsOn_${vid}`] && index.pages.length > 1
    let debuggerOn = {
      internals: Boolean(ui[`debuggerOn_internals_${vid}`]),
      images: Boolean(ui[`debuggerOn_images_${vid}`]),
    }
    //~ let Toggle = ToggleUI(ui, toggleUI)

    let activeLayers = mapItems(layers, (k, v) => k, (k, v) => v)

    return (
      <ViewerContext.Provider key={imageLocs.root_id} value={{imageLocs, vid, index}}>
        <Toolbar>
          <Toolbar.Group title="Navigation">
            <Navigation
              focused={focused}
              index={index}
              selected={selected}
              scrollToId={scrollToId}
            />
          </Toolbar.Group>
          <Toolbar.Group title="Zoom">
            <ZoomSlider zoom={zoom} alignZoom={alignZoom} />
          </Toolbar.Group>
          <XMenu.Toggle
            value={ui[`thumbsOn_${vid}`]}
            set={value => setUI({[`thumbsOn_${vid}`]: value})}
            icon="film"
            disabled={index.pages.length === 0}
          />
          <DebugFeature>
            <XMenu.Item icon="level up" onClick={() => setTitleListVisible(true)} />
            <XMenu.Toggle
              value={ui[`debuggerOn_internals_${vid}`]}
              set={value => setUI({[`debuggerOn_internals_${vid}`]: value})}
              icon="bug"
              className="debug"
              title="internals debugger"
            />
            <Debug imageLocs={imageLocs} index={index} />
            <LayerSwitcher layers={layers} onChange={layers => setLayers(layers)} />
          </DebugFeature>
          <Toolbar.Group position="right">
            <InbookSearch
              position="right"
              vid={vid}
              otid={imageLocs.root_id}
              viewer={ref_viewer.current}
              pages={index.pages}
            />
          </Toolbar.Group>
          <DebugFeature>
            <OptionSwitcher
              ui={ui}
              optionSet={optionSet}
              setUI={setUI}
              pages={index.pages}
              vid={vid}
            />
          </DebugFeature>
        </Toolbar>
        <PageNavigator
          imageLocs={imageLocs}
          index={index}
          selected={selected}
          scrollToId={scrollToId}
        />
        <div style={{flex: 1}} className="viewer">
          <Mordor
            debuggerOn={debuggerOn}
            imageLocs={imageLocs}
            optionSet={optionSet}
            activeLayers={activeLayers}
            viewerId={vid}
            initialScrollTo={{index: focused}}
            onSelectionChange={onViewerSelectionChange}
            onZoomChange={zoom => setZoom(zoom)}
            ref={ref_viewer}
          />
        </div>
        {thumbsActive && (
          <div className="thumbStripe">
            <Mordor
              imageLocs={imageLocs}
              optionSet="thumbs"
              viewerId={`thumbs_${vid}`}
              onSelectionChange={onThumbSelectionChange}
              marked={selected.center}
              ref={ref_thumbs}
            />
          </div>
        )}
      </ViewerContext.Provider>
    )
  }
)

export const goTreeViewer = (go, {odid, otid, pageid}) =>
  go({odid, classname: 'tree', tabname: 'viewer', id: pageid || otid})

const useViewerControl = ({scrollToPageRect, scrollToPageId, onViewerInstance}) => {
  /* instrument viewer
  also see renderController() as a alternative for controlling a viewer
  */
  let [initialized, setInitialized] = useState(false)
  // undebounced does not work in prod (only in slow dev)
  // but it's also better visually
  let [bouncedInitialized] = useDebounce(initialized, 400)
  let ref_viewer = useRef()
  useEffect(() => {
    if (scrollToPageRect && ref_viewer.current && bouncedInitialized) {
      let {id, x1, y1, x2, y2, align, scrollToMode, duration, keepZoom} = scrollToPageRect
      ref_viewer.current.scrollToAreaRect(id, x1, y1, x2, y2, {
        align,
        scrollToMode,
        duration,
        keepZoom,
      })
    }
  }, [scrollToPageRect, bouncedInitialized])

  useEffect(() => {
    if (scrollToPageId && ref_viewer.current) {
      let defaultKeepZoom = false
      let {id, ids, keepZoom = defaultKeepZoom, duration, scale, align} = isObject(scrollToPageId)
        ? scrollToPageId
        : {id: scrollToPageId, keepZoom: defaultKeepZoom}
      if (ids == 'ALL_IDS') {
        let {width, height} = ref_viewer.current.state.layout
        let [x1, y1, x2, y2] = [0, 0, width, height]
        if (scale) {
          // scale is given in percent
          let [scale_x, scale_y] = [(width / 100) * scale, (height / 100) * scale]
          ;[x1, y1, x2, y2] = [x1 - scale_x, y1 - scale_y, x2 + scale_x, y2 + scale_y]
        }
        ref_viewer.current.scrollToRect(x1, y1, x2, y2, {duration, align: 'top'})
      } else {
        ref_viewer.current.scrollToId({id, keepZoom, align})
      }
    }
  }, [scrollToPageId])

  useEffect(() => {
    callIfFunc(onViewerInstance, ref_viewer.current)
  }, [initialized])
  let onInitialized = useCallback(() => setInitialized(true))
  return {ref: ref_viewer, onInitialized}
}

const useThumbs = ({vid, imageLocs, viewerRef, selected}) => {
  /* inactive without imagelocs
   */
  let thumbs = null
  let thumbRef = useRef()

  let onThumbSelectionChange = ({change, selected: nextSelected}) => {
    if (size(change.clicked) > 0) {
      let id = change.clicked[0]
      if (viewerRef.current) {
        viewerRef.current.scrollToId({id, duration: 1.0, keepZoom: false})
      }
    }
  }

  if (imageLocs) {
    thumbs = (
      <div className="thumbStripe" style={{height: '100px', borderTop: '1px solid #CCC'}}>
        <Mordor
          imageLocs={imageLocs}
          optionSet="thumbs"
          viewerId={`thumbs_${vid}`}
          onSelectionChange={onThumbSelectionChange}
          marked={selected.center}
          ref={thumbRef}
        />
      </div>
    )
  }
  return {thumbsPane: thumbs, thumbRef}
}
export const PageSetViewer = ({
  otid, // root otid - like md-otid
  otids, // explicit list of page otids
  vid, // auto derived from otid/otids if not given
  optionSet = 'v1',
  options = {}, // more options
  activeLayers,
  scrollToPageRect, // on change: trigger scrollToAreaRect in viewer {id, x1, y1, x2, y2, align, duration}
  scrollToPageId, // scroll to this page id {id, keepZoom}
  initialScrollTo, // initial scroll to {index: 0, id: null}
  onPageClick,
  onRectSelect,
  onCenterSelectionChange,
  onDataLoaded,
  debug = false,
  height, // configure minHeight
  flex, // apply flex: 1
  loadOnVisible = false, // only load data if visible (useable if embedded in long scroll content like presentations)
  renderCustomPaginations,
  onViewerInstance, // external access on mordor instance
  renderController,
  thumbs, // false or 'below'  render another morder instance in thumbstripe config below
}) => {
  if (!vid) {
    if (otids) {
      vid = `pageset_${hashObj(otids)}`
    } else {
      vid = `pageset_${otid}`
    }
  }

  let {ref: viewerRef, onInitialized} = useViewerControl({
    scrollToPageRect,
    scrollToPageId,
    onViewerInstance,
  })

  let [divRef, inView] = useInView({triggerOnce: true})
  let loadEnabled = !loadOnVisible || inView

  let explicitEnabled = size(otids) > 0
  // from list of otids
  let locsExplicit = useImageLocsPageSet({vid, otids, enabled: explicitEnabled && loadEnabled})
  // from scalar otid
  let locsDescendants = useImageLocs({otid, enabled: !!otid && !explicitEnabled && loadEnabled})
  let imageLocs = explicitEnabled ? locsExplicit : locsDescendants
  let index = useMemo(() => indexImageLocs(imageLocs), [imageLocs])
  let loaded = !!imageLocs

  let [selected, setSelected] = useState({})
  let {thumbsPane, thumbRef} = useThumbs({
    vid,
    imageLocs: thumbs ? imageLocs : null,
    viewerRef,
    selected,
  })

  useEffect(() => {
    callIfFunc(onDataLoaded, {imageLocs, index})
  }, [imageLocs, index, onDataLoaded])

  if (loaded && initialScrollTo?.id) {
    // XXX: initialScrollTo still unsupported - transform
    let idx = index.byId[(initialScrollTo?.id)]?.flatIdx
    if (!idx) {
      // maybe a md id
      initialScrollTo = undefined
    } else {
      initialScrollTo = {index: idx}
    }
  }

  let onSelectionChange = ({change, selected}) => {
    if (change.clicked && onPageClick) {
      // will clicked ever grow a multi-clicked?
      onPageClick({otid: change.clicked[0]})
    }
    if (change.center && onCenterSelectionChange) {
      onCenterSelectionChange({otid: change.center})
    }
    if (thumbRef.current) {
      thumbRef.current.scrollToId({id: selected.center, duration: 0.2})
    }
    setSelected(selected)
  }
  if (onPageClick) {
    options = mergeDeep(options, {selection: {on: true, selectClick: true}})
  }

  let style = flex ? {flex: 1} : {}
  if (height) {
    style.minHeight = `${height}px`
  }

  // a stable vid will keep component flicker free
  // a recreated component will at least rerender once after mount due to internal width/height init
  return (
    <ViewerContext.Provider key={vid} value={{vid, imageLocs, index}}>
      <div style={style} className="viewer" ref={divRef}>
        {loaded && (
          <Mordor
            ref={viewerRef}
            imageLocs={imageLocs}
            initialScrollTo={initialScrollTo}
            activeLayers={activeLayers}
            viewerId={vid}
            optionSet={optionSet}
            options={{scrollTo: {bar: false}, ...options}}
            onSelectionChange={onSelectionChange}
            debuggerOn={{internals: debug}}
            onRectSelect={onRectSelect}
            renderCustomPaginations={renderCustomPaginations}
            onInitialized={onInitialized}
            renderController={renderController}
          />
        )}
      </div>
      {thumbs == 'below' && thumbsPane}
    </ViewerContext.Provider>
  )
}

export const SinglePageViewer = ({
  otid,
  vid, // viewer id, will reuse partName if omitted
  flex,
  height,
  activeLayers,
  zoomMode = 'areaFit',
  options,
  optionSet = 'single',
  scrollToPageRect,
  onRectSelect,
  debug = false,
  style,
  onViewerInstance,
  renderController,
}) => {
  /* simpler than PageSetViewer
   */
  scrollToPageRect = useMemo(() => {
    if (!scrollToPageRect) return scrollToPageRect
    // auto set otid
    return {...scrollToPageRect, id: otid}
  }, [scrollToPageRect])

  let {ref, onInitialized} = useViewerControl({scrollToPageRect, onViewerInstance})

  let {name: partName} = usePartContext()
  if (!vid) {
    vid = `viewer_${partName}`
  }

  let imageLocs = useImageLocsPageSet({vid, otids: [otid]})
  let index = useMemo(() => indexImageLocs(imageLocs), [imageLocs])

  let s = flex ? {flex: 1} : {}
  if (height) {
    s = {...s, minHeight: `${height}px`}
  }
  s = {...s, ...style}
  return (
    <ViewerContext.Provider key={`${imageLocs.id}_${otid}`} value={{imageLocs, vid, index}}>
      <div style={s} className="viewer">
        <Mordor
          ref={ref}
          imageLocs={imageLocs}
          viewerId={vid}
          activeLayers={activeLayers}
          optionSet={optionSet}
          options={{
            zoom: {mode: zoomMode},
            // less minZoom - depends on aspect ratio
            viewport: {virtual: 0.8},
            ...options,
          }}
          onRectSelect={onRectSelect}
          debuggerOn={{internals: debug}}
          onInitialized={onInitialized}
          renderController={renderController}
        />
      </div>
    </ViewerContext.Provider>
  )
}

export const BoundingBoxViewer = ({
  otid,
  bbox, // {x1,y1,x2,y2}
  vid,
  onChange,
  editable = false,
  isModified = false,
  options,
  ...props
}) => {
  /* one page with one editable bounding box
   */
  let {name: partName} = usePartContext()
  if (!vid) {
    vid = `viewer_${partName}`
  }

  // create geometry for vid
  let {modified: modifiedBBox, resetModified, setModified} = useBoundingBoxGeometry({
    vid,
    otid,
    bbox,
    isModified,
  })

  useEffect(() => {
    if (editable && modifiedBBox) {
      let {x1, y1, x2, y2} = modifiedBBox
      callIfFunc(onChange, {x1, y1, x2, y2})
    }
  }, [modifiedBBox])
  useEffect(() => {
    resetModified()
    // also reset on unmount
    return resetModified
  }, [otid])

  let onRectSelect = null
  if (editable) {
    options = mergeDeep(options || {}, {
      layers: {geometry: {resizable: true}},
      selection: {on: true, selectRect: true, selectRectZoom: false, selectRectKey: 'shift'},
      mouse: {dragScroll: true},
    })
    onRectSelect = ({pages}) => {
      if (pages[0]) {
        let {x1, y1, x2, y2} = pages[0]
        setModified({x1, y1, x2, y2})
      }
    }
  }
  return (
    <SinglePageViewer
      options={options}
      activeLayers={['geometry']}
      otid={otid}
      vid={vid}
      onRectSelect={onRectSelect}
      {...props}
    />
  )
}

export const HighlightingViewer = ({
  otid,
  bboxes, // [{x1,y1,x2,y2}, ]
  vid,
  options,
  ...props
}) => {
  /* one page with some bounding boxes
   */
  let {name: partName} = usePartContext()
  if (!vid) {
    vid = `viewer_${partName}`
  }
  // create geometry for vid
  useHighlightingGeometry({vid, otid, bboxes})
  let position = useMemo(() => {
    let x1 = Math.min(...bboxes.map(b => b[0]))
    let y1 = Math.min(...bboxes.map(b => b[1]))
    let x2 = Math.max(...bboxes.map(b => b[2]))
    let y2 = Math.max(...bboxes.map(b => b[3]))
    let xd = (x2 - x1) / 20
    x1 -= xd
    x2 += xd
    let yd = (y2 - x1) / 20
    y1 -= yd
    y2 += yd

    return {x1, y1, x2, y2, align: 'top'}
  }, [bboxes])

  return (
    <SinglePageViewer
      options={options}
      activeLayers={['geometry']}
      otid={otid}
      vid={vid}
      scrollToPageRect={position}
      {...props}
    />
  )
}
